"use client";

import { Box } from "@mui/material";
import { Component, ErrorInfo, ReactNode } from "react";

import { Log, StorageHelper } from "@/src/util";

import theme from "@/styles/scss/theme.module.scss";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  forceFallback?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidMount(): void {
    if (this.props.forceFallback) {
      this.handleRetry();
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Log.logToProvider(Log.LogLevel.ERROR, "ErrorBoundary.tsx", "ErrorBoundary", [error, errorInfo], "ErrorBoundary");
    this.handleRetry();
  }

  handleRetry = async (): Promise<void> => {
    if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
      const getFromStorage = await StorageHelper().getFromCache("ERROR_COUNT");
      const errorCount = parseInt(getFromStorage ?? "0");
      const thisErrorCount = isNaN(errorCount) ? 0 : errorCount;
      if (thisErrorCount < 4) {
        const ttl = 3 * 1 * 1000;
        StorageHelper().setToCache("ERROR_COUNT", thisErrorCount + 1, ttl);
        window.location.reload();
      }
    }
  };

  render(): ReactNode {
    if (this.state.hasError || this.props.forceFallback) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            backgroundColor: theme.secondaryMain,
          }}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
